import {
  fetchdropdownMall,
  fetchdropdownSafetyStatus,
  fetchdropdownTenant,
  DropdownPermitType,
  DropdownPermitStatus,
  DropdownPermitRequester,
  DropdownLoadingDoor,
  DropdownBrand,
} from "../services/dropdown";
import { tenantEmployee } from "../types/employee";
import { WorkPermitType } from "../types/workpermit";

interface DropdownItem {
  DataId: number;
  DataLabel: string;
}

interface DropdownOption {
  value: number;
  label: string;
}

export const fetchDropdownData = async (
  type: string,
  param: number | null = null
): Promise<DropdownOption[]> => {
  let response;
  let formatter: (d: DropdownItem[]) => DropdownOption[];
  switch (type) {
    case "mallOptions":
      response = await fetchdropdownMall(param || 0);
      formatter = (d: DropdownItem[]) =>
        d.map((i: DropdownItem) => ({ value: i.DataId, label: i.DataLabel }));
      break;
    case "tenantOptions":
      response = await fetchdropdownTenant(param || 0);
      formatter = (d: DropdownItem[]) =>
        d.map((i: DropdownItem) => ({ value: i.DataId, label: i.DataLabel }));
      break;
    case "brandOptions":
      response = await DropdownBrand(param || 0);
      formatter = (d: DropdownItem[]) =>
        d.map((i: DropdownItem) => ({ value: i.DataId, label: i.DataLabel }));
      break;
    case "safetyStatusOptions":
      response = await fetchdropdownSafetyStatus(param || 0);
      formatter = (d: DropdownItem[]) =>
        d.map((i: DropdownItem) => ({ value: i.DataId, label: i.DataLabel }));
      break;
    case "permitTypeOptions":
      response = await DropdownPermitType(param || 0);
      formatter = (d: DropdownItem[]) =>
        d.map((i: DropdownItem) => ({ value: i.DataId, label: i.DataLabel }));
      break;
    case "permitStatusOptions":
      response = await DropdownPermitStatus(param || 0);
      formatter = (d: DropdownItem[]) =>
        d.map((i: DropdownItem) => ({ value: i.DataId, label: i.DataLabel }));
      break;
    case "permitRequesterOptions":
      response = await DropdownPermitRequester(param || 0);
      formatter = (d: DropdownItem[]) =>
        d.map((i: DropdownItem) => ({ value: i.DataId, label: i.DataLabel }));
      break;
    case "loadingDoorOptions":
      response = await DropdownLoadingDoor(param || 0);
      formatter = (d: DropdownItem[]) =>
        d.map((i: DropdownItem) => ({ value: i.DataId, label: i.DataLabel }));
      break;
    default:
      return [];
  }
  if (response?.Code === 0) return formatter(response.dropdownClassDatas);
  throw new Error("Failed to fetch " + type);
};

export const formatDate = (iso?: string) => {
  if (!iso) return "-";
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(dt);
  } catch {
    return iso;
  }
};

export const formatDateDMY = (iso?: string) => {
  if (!iso) return "-";
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).format(dt).replace(",", "");
  } catch {
    return iso;
  }
};


export const toLocalISOString = (date: Date): string => {
  const tzoffset = date.getTimezoneOffset() * 60000;
  const localISOTime = new Date(date.getTime() - tzoffset).toISOString().slice(0, -1);
  return localISOTime;
};

export const getStatusColor = (status: string) => {
  switch (status) {
    case "New":
      return "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300";
    case "In Progress":
      return "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400";
    case "Done":
      return "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400";
    case "Close":
      return "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400";
    case "Reject":
      return "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400";
    default:
      return "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300";
  }
};

export function getInitials(name: string) {
  const p = name.trim().split(/\s+/);
  const [a, b] = [p[0] || "", p[1] || ""];
  return (a[0] || "").toUpperCase() + (b[0] || "").toUpperCase();
}

export const cx = (...c: (string | false | undefined)[]) =>
  c.filter(Boolean).join(" ");

const e = (s: string) => `"${(s || "").replace(/"/g, '""')}"`;

export function toCSV(r: tenantEmployee[]): string {
  const h = ["Employee ID", "Name", "Role", "Email", "Phone", "Status"];
  const l = r.map((v) =>
    [
      v.TenantEmployeeId,
      v.FullName,
      v.JobTitle,
      v.Email,
      v.PhoneNumber,
      v.AccessCardStatus,
    ]
      .map((x) => e(String(x)))
      .join(",")
  );
  return [h.join(","), ...l].join("\n");
}

export function toCSVTenantRequest(r: any[]): string {
  const h = ["Request Number", "Mall", "Tenant", "Notes", "Status", "Date"];
  const l = r.map((v) =>
    [
      v.RequestNumber,
      v.MallName,
      v.TenantName,
      v.Notes,
      v.RequestStatusName,
      v.CreatedOn,
    ]
      .map((x) => e(String(x || "")))
      .join(",")
  );
  return [h.join(","), ...l].join("\n");
}

export function download(f: string, t: string) {
  const b = new Blob([t], { type: "text/csv;charset=utf-8;" });
  const u = URL.createObjectURL(b);
  const a = document.createElement("a");
  a.href = u;
  a.download = f;
  a.click();
  URL.revokeObjectURL(u);
}

type SortDirection = "asc" | "desc";

export function sortData<T>(
  data: T[] | unknown,
  key: keyof T,
  dir: SortDirection
): T[] {
  if (!Array.isArray(data)) return [];
  return [...data].sort((a: T, b: T) => {
    const vA = a?.[key];
    const vB = b?.[key];
    const A = (vA !== null && vA !== undefined ? String(vA) : "").toLowerCase();
    const B = (vB !== null && vB !== undefined ? String(vB) : "").toLowerCase();
    if (A < B) return dir === "asc" ? -1 : 1;
    if (A > B) return dir === "asc" ? 1 : -1;
    return 0;
  });
}

type PermitStatus = WorkPermitType["PermitStatusDesc"];

const STATUS_COLOR: Record<PermitStatus, string> = {
  "Pending Approval":
    "text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20",
  "Approved":
    "text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-900/20",
  "Reject":
    "text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-900/20",
};

export const getWorkPermitStatusColor = (status: PermitStatus): string =>
  STATUS_COLOR[status] ??
  "text-gray-600 bg-gray-50 dark:text-gray-400 dark:bg-gray-900/20";

export const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("id-ID", { maximumFractionDigits: 0 }).format(amount);

export const MONTHS_OPTIONS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) =>
  String(new Date().getFullYear() - i)
);

export const clearAppCache = () => {
  try {
    // 1. Clear Cache Storage (Cache API)
    if ("caches" in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      }).catch(() => {});
    }

    // 2. Unregister Service Workers
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => {
          registration.unregister();
        });
      }).catch(() => {});
    }

    // 3. Clear Session Storage
    sessionStorage.clear();

    // 4. Clear Local Storage, keeping credentials if remember_me is true
    const rememberMe = localStorage.getItem("remember_me") === "true";
    const email = localStorage.getItem("user_email");
    const pwd = localStorage.getItem("user_password");

    localStorage.clear();

    if (rememberMe) {
      localStorage.setItem("remember_me", "true");
      if (email) localStorage.setItem("user_email", email);
      if (pwd) localStorage.setItem("user_password", pwd);
    }
  } catch (err) {
    console.error("Error clearing application cache:", err);
  }
};

export { resolveImageUrl } from "./imageHelper";

export function formatStatusLabel(label: string | undefined | null): string {
  if (!label) return "";
  const trimmed = label.trim();
  if (/^waiting/i.test(trimmed)) {
    if (trimmed.includes("-") || trimmed.includes(":")) {
      const parts = trimmed.split(/[-:]/);
      const lastPart = parts[parts.length - 1].trim();
      if (lastPart) return lastPart;
    }
    const cleaned = trimmed.replace(/^waiting\s+[-:]*\s*(for\s+)?/i, "").trim();
    return cleaned || trimmed;
  }
  return trimmed;
}

